/**
 * IncentiveProjectionCard
 *
 * Shows ADCOE quarterly bonus projection. Only rendered when
 * `incentiveProjection` is present in the leadership aggregation response
 * (i.e., the logged-in user has the equip.apc_lead role viewing their own team).
 *
 * Layout:
 *  - Header: "Your Quarterly Bonus Projection" with projected total vs max
 *  - Segmented progress bar (green slices = passed, red slices = failed)
 *  - Two sections: Outcome measures (6-7 rows) + Consistency measures (3 rows)
 *  - CRCS row is muted with "Data Pending" badge instead of pass/fail
 *  - Footer disclaimer about revenue gate
 */

import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Clock, TrendingUp, ChevronRight, Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { IncentiveProjection, IncentiveMeasure } from '@/lib/equip-types'
import type { ReactNode } from 'react'

/** Zero-denominator measures auto-pass regardless of category (passed=null, denom=0). */
function isAutoPass(m: IncentiveMeasure): boolean {
  return m.denominator === 0 && m.passed == null
}

interface IncentiveProjectionCardProps {
  projection: IncentiveProjection
  quarter: number
  onMeasureClick?: (measureName: string, measureCategory: string) => void
  activeMeasure?: string | null
  onExportClick?: () => void
  exportLoading?: boolean
  /** Render drill-down content inline below the active measure row */
  renderDrilldown?: (measureName: string, measureCategory: string, measure: IncentiveMeasure) => ReactNode
}

/** Format a dollar value like $1,000 or $1,234.56 */
function formatDollar(value: number): string {
  const isWhole = value % 1 === 0
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  })}`
}

/** Format a pass rate (0-1 scale) as a percentage string. */
function formatRate(rate: number | null): string {
  if (rate == null) return '—'
  return `${Math.round(rate * 1000) / 10}%`
}

/** Returns pass/fail/pending label and visual props for a measure row. */
function MeasureStatusBadge({ measure }: { measure: IncentiveMeasure }) {
  if (isAutoPass(measure)) {
    return (
      <Badge variant="secondary" className="text-xs gap-1 shrink-0">
        <Clock className="h-3 w-3" />
        No Data Available
      </Badge>
    )
  }
  if (measure.passed == null) {
    return (
      <Badge variant="secondary" className="text-xs gap-1 shrink-0">
        <Clock className="h-3 w-3" />
        Data Pending
      </Badge>
    )
  }
  if (measure.passed) {
    return (
      <Badge className="text-xs gap-1 shrink-0 bg-[var(--equip-pass-light)] text-[var(--equip-pass)] hover:bg-[var(--equip-pass-light)]">
        <CheckCircle2 className="h-3 w-3" />
        Passing
      </Badge>
    )
  }
  return (
    <Badge variant="destructive" className="text-xs gap-1 shrink-0 opacity-80">
      <XCircle className="h-3 w-3" />
      Not Met
    </Badge>
  )
}

/** Display name map for measure codes. */
const MEASURE_DISPLAY: Record<string, string> = {
  AWV: 'Annual Wellness Visit',
  A1C: 'A1C Control',
  BCS: 'Breast Cancer Screening',
  CRCS: 'Colorectal Cancer Screening',
  PHQ9: 'PHQ-9 Screening',
  HTN: 'Hypertension Control',
  ACP: 'Advance Care Planning',
  PIMs: 'PIMs / Medication Safety',
  Coding: 'Coding Accuracy',
  Quality_Audits: 'Signature Standards',
}

function getMeasureDisplayName(name: string): string {
  return MEASURE_DISPLAY[name] ?? name
}

/**
 * Single measure row: name, numerator/denominator, rate, payout, status.
 */
function MeasureRow({
  measure,
  index,
  onClick,
  isActive,
}: {
  measure: IncentiveMeasure
  index: number
  onClick?: () => void
  isActive?: boolean
}) {
  const autoPass = isAutoPass(measure)
  const isPending = !autoPass && measure.passed == null
  const isClickable = onClick && !isPending && !autoPass

  function hoverGlow(): string {
    if (autoPass) return 'hover:shadow-[0_0_20px_rgba(59,130,246,0.5)]'
    if (measure.passed) return 'hover:shadow-[0_0_20px_rgba(20,184,166,0.5)]'
    return 'hover:shadow-[0_0_20px_rgba(239,68,68,0.5)]'
  }

  const denomLabel = (() => {
    if (measure.denominator === 0) return null
    if (measure.measureCategory === 'Consistency' && measure.measureName === 'PIMs') {
      return `${measure.denominator} patients`
    }
    if (measure.measureCategory === 'Consistency') {
      return `${measure.numerator} of ${measure.denominator} audits`
    }
    return `${measure.numerator} of ${measure.denominator} patients`
  })()

  function rowBackground(): string {
    if (isPending) return 'text-muted-foreground'
    if (autoPass) return 'bg-blue-50 dark:bg-blue-500/20'
    if (measure.passed) return 'bg-green-50 dark:bg-teal-500/20'
    return 'bg-red-50 dark:bg-red-500/20'
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.25 }}
      className={cn(
        'flex items-center justify-between gap-3 py-2 px-3',
        isActive ? 'rounded-t-xl' : 'rounded-md',
        'border border-transparent',
        !isActive && rowBackground(),
        isClickable && !isActive && `cursor-pointer transition-shadow duration-300 ${hoverGlow()}`,
        isActive && 'cursor-pointer',
      )}
      onClick={isClickable ? onClick : undefined}
    >
      {isClickable && (
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
            isActive && 'rotate-90 text-primary'
          )}
        />
      )}
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium truncate', isPending && 'text-muted-foreground')}>
          {getMeasureDisplayName(measure.measureName)}
        </p>
        {denomLabel && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {isPending ? 'No data available yet' : denomLabel}
            {!isPending && measure.passRate !== null && (
              <span className="ml-1 font-medium">
                ({formatRate(measure.passRate)})
              </span>
            )}
          </p>
        )}
        {!isPending && !autoPass && measure.denominator > 0 && measure.passRate !== null && (
          <div className="relative h-1.5 mt-1.5 rounded-full overflow-hidden bg-muted">
            <motion.div
              className="h-full"
              style={{ backgroundColor: measure.passed ? 'var(--equip-pass-light)' : 'var(--equip-fail-light)' }}
              initial={{ width: '0%' }}
              animate={{ width: `${Math.round(measure.passRate * 100)}%` }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.04 }}
            />
            <div
              className="absolute top-0 bottom-0 border-l-2 border-dashed border-muted-foreground/50"
              style={{ left: `${measure.thresholdDisplay}%` }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {!isPending && measure.payoutAmount > 0 && (
          <span
            className={cn(
              'text-sm font-semibold tabular-nums',
              measure.passed
                ? 'text-[var(--equip-pass)]'
                : autoPass
                  ? 'text-muted-foreground'
                  : 'text-destructive/70'
            )}
          >
            {(measure.passed || autoPass) ? '+' : '-'}{formatDollar(measure.payoutAmount)}
          </span>
        )}
        <MeasureStatusBadge measure={measure} />
      </div>
    </motion.div>
  )
}

/**
 * Segmented progress bar visualizing passed vs failed payout slices.
 * Each measure that has a payout_amount > 0 contributes one segment.
 * Passed = green, Failed = red/muted, Pending = gray.
 */
function ProjectionProgressBar({ projection, visibleOutcomeMeasures }: { projection: IncentiveProjection; visibleOutcomeMeasures: IncentiveMeasure[] }) {
  const allMeasures = [...visibleOutcomeMeasures, ...projection.consistencyMeasures]
  const totalPossible = projection.maxQuarterlyAward
  const segments = allMeasures.filter((m) => m.payoutAmount > 0)

  return (
    <div className="space-y-1.5">
      <div className="flex h-3 w-full overflow-hidden rounded-full gap-0.5 bg-muted/40">
        {segments.map((m, i) => {
          const widthPct = (m.payoutAmount / totalPossible) * 100
          const autoPassSeg = isAutoPass(m)
          const pendingSeg = !autoPassSeg && m.passed == null

          function segmentColor(): string {
            if (pendingSeg) return 'bg-muted'
            if (autoPassSeg) return 'bg-blue-400/60'
            if (m.passed) return 'bg-[var(--equip-pass)]'
            return 'bg-destructive/40'
          }

          function segmentLabel(): string {
            if (pendingSeg) return 'Pending'
            if (autoPassSeg) return 'Auto-Pass'
            if (m.passed) return 'Passing'
            return 'Not Met'
          }

          return (
            <motion.div
              key={m.measureName}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: i * 0.04 + 0.1, duration: 0.3, ease: 'easeOut' }}
              style={{ width: `${widthPct}%`, transformOrigin: 'left' }}
              className={cn('h-full rounded-sm', segmentColor())}
              title={`${getMeasureDisplayName(m.measureName)}: ${segmentLabel()}`}
            />
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>$0</span>
        <span>{formatDollar(totalPossible)}</span>
      </div>
    </div>
  )
}

function MeasureSection({
  measures,
  indexOffset,
  activeMeasure,
  onMeasureClick,
  renderDrilldown,
}: {
  measures: IncentiveMeasure[]
  indexOffset: number
  activeMeasure?: string | null
  onMeasureClick?: (measureName: string, measureCategory: string) => void
  renderDrilldown?: (measureName: string, measureCategory: string, measure: IncentiveMeasure) => ReactNode
}) {
  return (
    <div className="space-y-1">
      {measures.map((m, i) => {
        const isActive = activeMeasure === m.measureName
        const accentGradient = m.denominator === 0 && m.passed == null
          ? 'var(--equip-gradient-zero)'
          : m.passed
            ? 'var(--equip-gradient-pass)'
            : 'var(--equip-gradient-fail)'
        return (
          <div
            key={m.measureName}
            className={cn(
              'rounded-xl transition-shadow duration-300',
              isActive
                ? 'ring-1 ring-slate-200/60 dark:ring-slate-700/60 shadow-md mb-2'
                : 'ring-0 shadow-none',
            )}
            style={isActive ? {
              background: `linear-gradient(to bottom, ${accentGradient} 0%, var(--background) 40%)`,
            } : undefined}
          >
            <MeasureRow
              measure={m}
              index={indexOffset + i}
              onClick={onMeasureClick ? () => onMeasureClick(m.measureName, m.measureCategory) : undefined}
              isActive={isActive}
            />
            <AnimatePresence initial={false}>
              {isActive && renderDrilldown && (
                <motion.div
                  key={`drilldown-${m.measureName}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
                    opacity: { duration: 0.2, delay: 0.05 },
                  }}
                  className="overflow-hidden rounded-b-xl"
                  style={{ clipPath: 'inset(0)' }}
                >
                  <div className="px-5 pb-5 pt-1 space-y-4">
                    {renderDrilldown(m.measureName, m.measureCategory, m)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

export function IncentiveProjectionCard({
  projection,
  quarter,
  onMeasureClick,
  activeMeasure,
  onExportClick,
  exportLoading,
  renderDrilldown,
}: IncentiveProjectionCardProps) {
  // Q1 does not include CRCS as an active measure — hide it rather than showing "Data Pending"
  const visibleOutcomeMeasures = quarter === 1
    ? projection.outcomeMeasures.filter((m) => m.measureName !== 'CRCS')
    : projection.outcomeMeasures

  const passedOutcome = visibleOutcomeMeasures.filter(
    (m) => m.passed === true || isAutoPass(m)
  ).length
  const totalOutcome = visibleOutcomeMeasures.filter(
    (m) => m.passed !== null || isAutoPass(m)
  ).length
  const passedConsistency = projection.consistencyMeasures.filter(
    (m) => m.passed === true || isAutoPass(m)
  ).length
  const totalConsistency = projection.consistencyMeasures.filter(
    (m) => m.passed !== null || isAutoPass(m)
  ).length

  const outcomePayout = visibleOutcomeMeasures
    .filter((m) => m.passed === true || isAutoPass(m))
    .reduce((sum, m) => sum + m.payoutAmount, 0)

  const consistencyPayout = projection.consistencyMeasures
    .filter((m) => m.passed === true || isAutoPass(m))
    .reduce((sum, m) => sum + m.payoutAmount, 0)

  const outcomePotential = visibleOutcomeMeasures.reduce((sum, m) => sum + m.payoutAmount, 0)
  const consistencyPotential = projection.consistencyMeasures.reduce((sum, m) => sum + m.payoutAmount, 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-[var(--equip-pass)]" />
                Your Quarterly Bonus Projection
              </CardTitle>
              {projection.careTeam && (
                <p className="text-sm text-muted-foreground mt-0.5">{projection.careTeam}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onExportClick && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExportClick}
                  disabled={exportLoading}
                  className="h-8 gap-1.5 text-xs"
                >
                  <Download className={cn('h-3.5 w-3.5', exportLoading && 'animate-pulse')} />
                  Download Spreadsheet
                </Button>
              )}
              <div className="text-right">
                <p className="text-2xl font-bold tabular-nums">
                  {formatDollar(projection.projectedTotal)}
                </p>
                <p className="text-xs text-muted-foreground">
                  of {formatDollar(projection.maxQuarterlyAward)} max
                </p>
              </div>
            </div>
          </div>

          {/* Summary chips */}
          <div className="flex flex-wrap gap-2 mt-2">
            <div className="text-xs bg-muted rounded-md px-2 py-1">
              <span className="font-medium">Outcomes:</span>{' '}
              {passedOutcome} of {totalOutcome} passing
              <span className="ml-1 text-[var(--equip-pass)] font-semibold">
                {outcomePayout > 0 && `+${formatDollar(outcomePayout)}`}
              </span>
            </div>
            <div className="text-xs bg-muted rounded-md px-2 py-1">
              <span className="font-medium">Consistency:</span>{' '}
              {passedConsistency} of {totalConsistency} passing
              <span className="ml-1 text-[var(--equip-pass)] font-semibold">
                {consistencyPayout > 0 && `+${formatDollar(consistencyPayout)}`}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <ProjectionProgressBar projection={projection} visibleOutcomeMeasures={visibleOutcomeMeasures} />
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-4">
          {/* Outcome measures */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Outcome Measures — {formatDollar(outcomePotential)} potential
            </p>
            <MeasureSection
              measures={visibleOutcomeMeasures}
              indexOffset={0}
              activeMeasure={activeMeasure}
              onMeasureClick={onMeasureClick}
              renderDrilldown={renderDrilldown}
            />
          </div>

          {/* Consistency measures */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Consistency Measures — {formatDollar(consistencyPotential)} potential
            </p>
            <MeasureSection
              measures={projection.consistencyMeasures}
              indexOffset={visibleOutcomeMeasures.length}
              activeMeasure={activeMeasure}
              onMeasureClick={onMeasureClick}
              renderDrilldown={renderDrilldown}
            />
          </div>

          {/* Revenue gate disclaimer */}
          <p className="text-xs text-muted-foreground border-t pt-3">
            Subject to revenue gate — final payout may be adjusted based on panel revenue
            performance. This projection reflects quality performance only.
          </p>
        </CardContent>
      </Card>
    </motion.div>
  )
}
